import { LitElement, html, nothing } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { currentLang, t } from '../i18n';
import { api } from '../api';
import { loadRuntimeCapabilities } from '../runtime-capabilities';
import { showToast } from '../toast';
import { defineZenComponent } from './register';
import {
  canShowAdminControls,
  errorCodeToMessageKey,
  invitationStatusPresentation,
  memberDisplayName,
  roleIcon,
} from '../workspace-panel-logic';
import type {
  Workspace,
  WorkspaceInvitationPublic,
  WorkspaceInvitationRole,
  WorkspaceMemberRecord,
  WorkspaceMembership,
  WorkspaceMemberRole,
  WorkspacePermissions,
} from '../types';

interface WorkspacePanelState {
  open: boolean;
  loading: boolean;
  error: string;
  featureUnavailable: boolean;
  workspace: Workspace | null;
  viewerMembership: WorkspaceMembership | null;
  permissions: WorkspacePermissions | null;
  memberCount: number;
  members: WorkspaceMemberRecord[];
  invitations: WorkspaceInvitationPublic[];
}

interface InviteDraft {
  email: string;
  role: WorkspaceInvitationRole;
  submitting: boolean;
}

interface PendingRemoval {
  userId: string;
  displayName: string;
}

interface PendingRevoke {
  invitationId: string;
  targetEmail: string;
}

const ROLE_LABEL_KEY: Record<WorkspaceMemberRole, string> = {
  owner: 'workspaceRoleOwner',
  admin: 'workspaceRoleAdmin',
  member: 'workspaceRoleMember',
  guest: 'workspaceRoleGuest',
};

function defaultState(): WorkspacePanelState {
  return {
    open: false,
    loading: false,
    error: '',
    featureUnavailable: false,
    workspace: null,
    viewerMembership: null,
    permissions: null,
    memberCount: 0,
    members: [],
    invitations: [],
  };
}

function memberProfileName(member: WorkspaceMemberRecord): string | null {
  return member.profile.display_name ?? null;
}

export class ZenWorkspacePanel extends LitElement {
  static properties = {
    embedded: { type: Boolean, reflect: true },
    _state: { state: true },
    _inviteDraft: { state: true },
    _pendingRemoval: { state: true },
    _pendingRevoke: { state: true },
    _roleUpdatingUserId: { state: true },
  };

  declare embedded: boolean;
  declare _state: WorkspacePanelState;
  declare _inviteDraft: InviteDraft;
  declare _pendingRemoval: PendingRemoval | null;
  declare _pendingRevoke: PendingRevoke | null;
  declare _roleUpdatingUserId: string | null;

  private requestGeneration = 0;
  private focusReturnTarget: HTMLElement | null = null;

  constructor() {
    super();
    this.embedded = false;
    this._state = defaultState();
    this._inviteDraft = { email: '', role: 'member', submitting: false };
    this._pendingRemoval = null;
    this._pendingRevoke = null;
    this._roleUpdatingUserId = null;
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.onKeyDown);
    if (this.embedded) void this.openWithAvailabilityCheck();
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.onKeyDown);
    this.invalidatePendingWork();
    this.focusReturnTarget = null;
    super.disconnectedCallback();
  }

  private onKeyDown = (event: KeyboardEvent) => {
    const dialog = this.querySelector<HTMLElement>('[role="alertdialog"]');
    if (!dialog) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this._pendingRemoval) this._cancelRemoval();
      else this._cancelRevoke();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )).filter((element) => element.getClientRects().length > 0);
    if (focusable.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  private rememberFocus() {
    const active = document.activeElement;
    this.focusReturnTarget = active instanceof HTMLElement && this.contains(active) ? active : null;
  }

  private restoreFocus() {
    const target = this.focusReturnTarget;
    this.focusReturnTarget = null;
    if (target?.isConnected) requestAnimationFrame(() => target.focus());
  }

  private invalidatePendingWork() {
    this.requestGeneration += 1;
    this._state = { ...defaultState(), open: this.embedded };
    this._inviteDraft = { email: '', role: 'member', submitting: false };
    this._pendingRemoval = null;
    this._pendingRevoke = null;
    this._roleUpdatingUserId = null;
  }

  private isCurrentRequest(generation: number): boolean {
    return generation === this.requestGeneration && this.isConnected;
  }

  protected updated(changed: Map<PropertyKey, unknown>) {
    if (!changed.has('_pendingRemoval') && !changed.has('_pendingRevoke')) return;
    if (this._pendingRemoval || this._pendingRevoke) {
      requestAnimationFrame(() => this.querySelector<HTMLElement>('[role="alertdialog"]')?.focus());
    }
  }

  // Public load entry point used by the embedded settings section and its
  // retry action. It returns false when the workspace detail could not load.
  async openWithAvailabilityCheck(): Promise<boolean> {
    const generation = ++this.requestGeneration;
    this._state = { ...defaultState(), open: true, loading: true };
    this._pendingRemoval = null;
    this._pendingRevoke = null;
    this._inviteDraft = { email: '', role: 'member', submitting: false };

    try {
      const capabilities = await loadRuntimeCapabilities();
      if (!capabilities.workspace_admin) {
        if (!this.isCurrentRequest(generation)) return false;
        this._state = {
          ...defaultState(),
          open: this.embedded,
          error: t('workspaceUnavailable') as string,
          featureUnavailable: true,
        };
        return false;
      }
      const me = await api('/api/workspaces/me');
      if (!this.isCurrentRequest(generation)) return false;
      this._state = {
        ...this._state,
        workspace: me.workspace,
        viewerMembership: me.viewer_membership,
        permissions: me.permissions,
        memberCount: me.member_count,
      };
      await this._loadMembersAndInvitations(generation);
      return this.isCurrentRequest(generation);
    } catch (err) {
      if (!this.isCurrentRequest(generation)) return false;
      this._state = {
        ...defaultState(),
        open: this.embedded,
        // The deployment capability is the sole source for feature
        // availability. A successful ON config followed by a workspace
        // read failure is an ordinary retryable error, even when the error
        // happens to be a 404.
        error: this._messageForError(err),
        featureUnavailable: false,
      };
      return false;
    } finally {
      if (this.isCurrentRequest(generation)) this._state = { ...this._state, loading: false };
    }
  }

  close() {
    this.invalidatePendingWork();
    if (!this.embedded) this._state = { ...this._state, open: false };
  }

  private async _loadMembersAndInvitations(generation: number) {
    const workspaceId = this._state.workspace?.id;
    if (!workspaceId || !this.isCurrentRequest(generation)) return;
    const viewMode = this._state.permissions?.can_view_admin_profiles ? 'admin' : 'member';
    try {
      const membersRes = await api(`/api/workspaces/${workspaceId}/members?view=${viewMode}`);
      if (!this.isCurrentRequest(generation)) return;
      this._state = { ...this._state, members: membersRes.members ?? [] };
    } catch (err) {
      if (!this.isCurrentRequest(generation)) return;
      this._state = { ...this._state, error: this._messageForError(err) };
    }

    if (this.isCurrentRequest(generation) && canShowAdminControls(this._state.permissions)) {
      try {
        const invRes = await api(`/api/workspaces/${workspaceId}/invitations`);
        if (!this.isCurrentRequest(generation)) return;
        this._state = { ...this._state, invitations: invRes.invitations ?? [] };
      } catch (err) {
        if (!this.isCurrentRequest(generation)) return;
        this._state = { ...this._state, error: this._messageForError(err) };
      }
    }
  }

  private _messageForError(err: unknown): string {
    const code = (err as { code?: string } | null)?.code;
    if (code) return t(errorCodeToMessageKey(code)) as string;
    return t(errorCodeToMessageKey(undefined)) as string;
  }

  private async _submitInvite(event: Event) {
    event.preventDefault();
    const generation = this.requestGeneration;
    const workspaceId = this._state.workspace?.id;
    if (!workspaceId || this._inviteDraft.submitting) return;
    const email = this._inviteDraft.email.trim();
    if (!email) return;

    this._inviteDraft = { ...this._inviteDraft, submitting: true };
    try {
      const res = await api(`/api/workspaces/${workspaceId}/invitations`, {
        method: 'POST',
        json: { email, role: this._inviteDraft.role },
      });
      if (!this.isCurrentRequest(generation)) return;
      this._state = {
        ...this._state,
        invitations: [res.invitation, ...this._state.invitations],
      };
      this._inviteDraft = { email: '', role: 'member', submitting: false };
      this._reportDelivery(res.delivery, 'workspaceInviteCreateSuccess', 'workspaceInviteDeliveryFailed');
    } catch (err) {
      if (!this.isCurrentRequest(generation)) return;
      showToast(this._messageForError(err), 'error');
      this._inviteDraft = { ...this._inviteDraft, submitting: false };
    }
  }

  private async _resendInvite(invitation: WorkspaceInvitationPublic) {
    const generation = this.requestGeneration;
    const workspaceId = this._state.workspace?.id;
    if (!workspaceId) return;
    try {
      const res = await api(`/api/workspaces/${workspaceId}/invitations/${invitation.id}/resend`, { method: 'POST' });
      if (!this.isCurrentRequest(generation)) return;
      this._state = {
        ...this._state,
        invitations: this._state.invitations.map((inv) => (inv.id === invitation.id ? res.invitation : inv)),
      };
      this._reportDelivery(res.delivery, 'workspaceInviteResendSuccess', 'workspaceInviteResendDeliveryFailed');
    } catch (err) {
      if (!this.isCurrentRequest(generation)) return;
      showToast(this._messageForError(err), 'error');
    }
  }

  private _requestRevoke(invitation: WorkspaceInvitationPublic) {
    this.rememberFocus();
    this._pendingRevoke = { invitationId: invitation.id, targetEmail: invitation.target_email };
  }

  private _cancelRevoke() {
    this._pendingRevoke = null;
    this.restoreFocus();
  }

  private async _confirmRevoke() {
    const generation = this.requestGeneration;
    const pending = this._pendingRevoke;
    const workspaceId = this._state.workspace?.id;
    if (!pending || !workspaceId) return;
    try {
      const res = await api(`/api/workspaces/${workspaceId}/invitations/${pending.invitationId}`, { method: 'DELETE' });
      if (!this.isCurrentRequest(generation)) return;
      this._state = {
        ...this._state,
        invitations: this._state.invitations.map((inv) => (inv.id === pending.invitationId ? res.invitation : inv)),
      };
      showToast(t('workspaceInviteRevokeSuccess') as string, 'info');
    } catch (err) {
      if (!this.isCurrentRequest(generation)) return;
      showToast(this._messageForError(err), 'error');
    } finally {
      if (this.isCurrentRequest(generation)) {
        this._pendingRevoke = null;
        this.restoreFocus();
      }
    }
  }

  // The invitation mail is the only carrier of the token. The server reports
  // whether it went out; a failed send leaves the invitation pending for resend.
  private _reportDelivery(delivery: { status?: string } | undefined, sentKey: string, failedKey: string) {
    if (delivery?.status === 'sent') {
      showToast(t(sentKey) as string, 'info');
      return;
    }
    showToast(t(failedKey) as string, 'error');
  }

  private async _changeRole(member: WorkspaceMemberRecord, newRole: WorkspaceMemberRole) {
    const generation = this.requestGeneration;
    const workspaceId = this._state.workspace?.id;
    if (!workspaceId || newRole === member.role) return;
    this._roleUpdatingUserId = member.user_id;
    try {
      const res = await api(`/api/workspaces/${workspaceId}/members/${member.user_id}`, {
        method: 'PATCH',
        json: { role: newRole },
      });
      if (!this.isCurrentRequest(generation)) return;
      this._state = {
        ...this._state,
        members: this._state.members.map((m) => (m.user_id === member.user_id ? { ...m, role: res.member.role } : m)),
      };
      showToast(t('workspaceRoleUpdateSuccess') as string, 'info');
    } catch (err) {
      if (!this.isCurrentRequest(generation)) return;
      showToast(this._messageForError(err), 'error');
    } finally {
      if (this.isCurrentRequest(generation)) this._roleUpdatingUserId = null;
    }
  }

  private _requestRemoval(member: WorkspaceMemberRecord) {
    this.rememberFocus();
    this._pendingRemoval = {
      userId: member.user_id,
      displayName: memberDisplayName(memberProfileName(member), 'workspaceMemberFallbackName', (k) => t(k) as string),
    };
  }

  private _cancelRemoval() {
    this._pendingRemoval = null;
    this.restoreFocus();
  }

  private async _confirmRemoval() {
    const generation = this.requestGeneration;
    const pending = this._pendingRemoval;
    const workspaceId = this._state.workspace?.id;
    if (!pending || !workspaceId) return;
    try {
      await api(`/api/workspaces/${workspaceId}/members/${pending.userId}`, { method: 'DELETE' });
      if (!this.isCurrentRequest(generation)) return;
      this._state = { ...this._state, members: this._state.members.filter((m) => m.user_id !== pending.userId) };
      showToast(t('workspaceRemoveMemberSuccess') as string, 'info');
    } catch (err) {
      if (!this.isCurrentRequest(generation)) return;
      showToast(this._messageForError(err), 'error');
    } finally {
      if (this.isCurrentRequest(generation)) {
        this._pendingRemoval = null;
        this.restoreFocus();
      }
    }
  }

  private _renderMemberRow(member: WorkspaceMemberRecord) {
    const isAdmin = canShowAdminControls(this._state.permissions);
    const name = memberDisplayName(memberProfileName(member), 'workspaceMemberFallbackName', (k) => t(k) as string);
    const { icon, colorVar } = roleIcon(member.role);
    const isOwner = member.role === 'owner';
    const updating = this._roleUpdatingUserId === member.user_id;

    return html`
      <div class="workspace-member-row" style="display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid var(--border);">
        <span class="mi mi-sm" style=${`color:${colorVar};`}>${icon}</span>
        <div style="flex:1;min-width:0;">
          <div style="font-size:13px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${name}</div>
          <div style="font-size:11px;color:var(--text-muted);">${t(ROLE_LABEL_KEY[member.role])}</div>
        </div>
        ${isAdmin && !isOwner ? html`
          <select
            aria-label=${t('workspaceChangeRole') as string}
            .value=${member.role}
            ?disabled=${updating}
            style="font-size:12px;padding:4px 6px;border:1px solid var(--border);border-radius:var(--radius);background:var(--surface);"
            @change=${(e: Event) => this._changeRole(member, (e.target as HTMLSelectElement).value as WorkspaceMemberRole)}
          >
            <option value="admin" ?selected=${member.role === 'admin'}>${t('workspaceRoleAdmin')}</option>
            <option value="member" ?selected=${member.role === 'member'}>${t('workspaceRoleMember')}</option>
            <option value="guest" ?selected=${member.role === 'guest'}>${t('workspaceRoleGuest')}</option>
          </select>
          <button
            class="workspace-remove-btn"
            title=${t('workspaceRemoveMember') as string}
            style="background:none;border:none;color:var(--danger);cursor:pointer;padding:4px;"
            @click=${() => this._requestRemoval(member)}
          >
            <span class="mi mi-sm">person_remove</span>
          </button>
        ` : nothing}
      </div>
    `;
  }

  private _renderInviteRow(invitation: WorkspaceInvitationPublic) {
    const presentation = invitationStatusPresentation(invitation);
    const isPending = invitation.status === 'pending';

    return html`
      <div class="workspace-invite-row" style="padding:10px 0;border-bottom:1px solid var(--border);">
        <div style="display:flex;align-items:center;gap:10px;">
          <span class="mi mi-sm" style=${`color:${presentation.colorVar};`}>${presentation.icon}</span>
          <div style="flex:1;min-width:0;">
            <div style="font-size:13px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${invitation.target_email}</div>
            <div style="font-size:11px;color:var(--text-muted);">
              ${t(presentation.messageKey)}
              ${isPending ? html` · ${t('workspaceInviteExpiresAt', { date: new Date(invitation.expires_at).toLocaleDateString(currentLang) })}` : nothing}
            </div>
          </div>
          ${isPending ? html`
            <button class="btn-text" style="font-size:12px;color:var(--accent);background:none;border:none;cursor:pointer;" @click=${() => this._resendInvite(invitation)}>${t('workspaceInviteResendBtn')}</button>
            <button class="btn-text" style="font-size:12px;color:var(--danger);background:none;border:none;cursor:pointer;" @click=${() => this._requestRevoke(invitation)}>${t('workspaceInviteRevokeBtn')}</button>
          ` : nothing}
        </div>
      </div>
    `;
  }

  private _renderInviteForm() {
    return html`
      <form style="display:flex;gap:8px;margin:12px 0;" @submit=${(e: Event) => this._submitInvite(e)}>
        <input
          type="email"
          required
          placeholder=${t('workspaceInviteEmailPlaceholder') as string}
          .value=${this._inviteDraft.email}
          style="flex:1;padding:6px 8px;border:1px solid var(--border);border-radius:var(--radius);font-size:13px;background:var(--surface);"
          @input=${(e: Event) => { this._inviteDraft = { ...this._inviteDraft, email: (e.target as HTMLInputElement).value }; }}
        >
        <select
          aria-label=${t('workspaceInviteRoleLabel') as string}
          .value=${this._inviteDraft.role}
          style="padding:6px 8px;border:1px solid var(--border);border-radius:var(--radius);font-size:13px;background:var(--surface);"
          @change=${(e: Event) => { this._inviteDraft = { ...this._inviteDraft, role: (e.target as HTMLSelectElement).value as WorkspaceInvitationRole }; }}
        >
          <option value="member" ?selected=${this._inviteDraft.role === 'member'}>${t('workspaceRoleMember')}</option>
          <option value="admin" ?selected=${this._inviteDraft.role === 'admin'}>${t('workspaceRoleAdmin')}</option>
          <option value="guest" ?selected=${this._inviteDraft.role === 'guest'}>${t('workspaceRoleGuest')}</option>
        </select>
        <button type="submit" class="btn btn-primary" style="font-size:13px;padding:6px 14px;" ?disabled=${this._inviteDraft.submitting}>
          ${this._inviteDraft.submitting ? html`<span class="spinner spinner-white"></span>` : t('workspaceInviteSubmitBtn')}
        </button>
      </form>
      <div style="font-size:11px;color:var(--text-muted);margin:-6px 0 10px;">${t('workspaceInviteMailNote')}</div>
    `;
  }

  private _renderRemovalConfirm() {
    const pending = this._pendingRemoval;
    if (!pending) return nothing;
    return html`
      <div class="modal-overlay confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="workspaceRemoveMemberDialogTitle" tabindex="-1" @click=${(e: Event) => { if (e.target === e.currentTarget) this._cancelRemoval(); }}>
        <div class="modal-content">
          <div class="modal-body">
            <div class="confirm-icon"><span class="mi">person_remove</span></div>
            <div class="confirm-msg" id="workspaceRemoveMemberDialogTitle">${t('workspaceRemoveMemberConfirm', { name: pending.displayName })}</div>
            <div class="confirm-actions">
              <button type="button" class="confirm-cancel" @click=${() => this._cancelRemoval()}>${t('workspaceRemoveMemberCancelBtn')}</button>
              <button type="button" class="confirm-ok" @click=${() => this._confirmRemoval()}>${t('workspaceRemoveMemberConfirmBtn')}</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderRevokeConfirm() {
    const pending = this._pendingRevoke;
    if (!pending) return nothing;
    return html`
      <div class="modal-overlay confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="workspaceInviteRevokeDialogTitle" tabindex="-1" @click=${(e: Event) => { if (e.target === e.currentTarget) this._cancelRevoke(); }}>
        <div class="modal-content">
          <div class="modal-body">
            <div class="confirm-icon"><span class="mi">block</span></div>
            <div class="confirm-msg" id="workspaceInviteRevokeDialogTitle">${t('workspaceInviteRevokeConfirm', { email: pending.targetEmail })}</div>
            <div class="confirm-actions">
              <button type="button" class="confirm-cancel" @click=${() => this._cancelRevoke()}>${t('workspaceRemoveMemberCancelBtn')}</button>
              <button type="button" class="confirm-ok" @click=${() => this._confirmRevoke()}>${t('workspaceInviteRevokeConfirmBtn')}</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderBody(s: WorkspacePanelState, isAdmin: boolean) {
    if (s.loading) return html`<div style="display:flex;justify-content:center;padding:24px;"><span class="spinner"></span></div>`;

    return html`
      <div style="display:flex;align-items:flex-start;gap:8px;padding:10px 12px;border-radius:var(--radius);background:color-mix(in srgb, var(--accent) 8%, transparent);margin-bottom:16px;">
        <span class="mi mi-sm" style="color:var(--accent);">info</span>
        <div style="font-size:12px;color:var(--text);line-height:1.5;">${t('workspaceSharedNotice')}</div>
      </div>

      ${s.error ? html`
        <div class="msg msg-error show" role="alert" style="margin-bottom:12px;">${s.error}</div>
        ${!s.featureUnavailable ? html`
          <button type="button" class="btn btn-secondary" style="margin-bottom:12px;" @click=${() => this.openWithAvailabilityCheck()}>
            <span class="mi mi-sm">refresh</span>${t('retry')}
          </button>
        ` : nothing}
      ` : nothing}

      ${s.workspace ? html`
        <div style="font-size:13px;color:var(--text-muted);margin-bottom:8px;">${t('workspaceMemberCount', { count: s.memberCount })}</div>

        <div class="workspace-members-section">
          <label class="field-label" style="display:block;font-size:13px;font-weight:500;margin-bottom:4px;">${t('workspaceMembersHeading')}</label>
          ${s.members.length === 0 ? html`<div style="font-size:12px;color:var(--text-muted);padding:8px 0;">${t('workspaceNoMembers')}</div>` : s.members.map((m) => this._renderMemberRow(m))}
        </div>

        ${isAdmin ? html`
          <div class="workspace-invitations-section" style="margin-top:20px;">
            <label class="field-label" style="display:block;font-size:13px;font-weight:500;margin-bottom:4px;">${t('workspaceInvitationsHeading')}</label>
            ${this._renderInviteForm()}
            ${s.invitations.length === 0 ? html`<div style="font-size:12px;color:var(--text-muted);padding:8px 0;">${t('workspaceNoInvitations')}</div>` : s.invitations.map((inv) => this._renderInviteRow(inv))}
          </div>
        ` : nothing}
      ` : nothing}
    `;
  }

  render() {
    const s = this._state;
    const isAdmin = canShowAdminControls(s.permissions);
    const body = this._renderBody(s, isAdmin);

    return keyed(currentLang, this.embedded ? html`
      <div class="workspace-panel-embedded">
        ${body}
      </div>
      ${this._renderRemovalConfirm()}
      ${this._renderRevokeConfirm()}
    ` : html`
      <div
        class="modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="workspacePanelTitle"
        style=${s.open ? 'display:flex' : 'display:none'}
        @click=${(event: Event) => { if (event.target === event.currentTarget) this.close(); }}
      >
        <div class="modal-content" style="max-width:560px;">
          <div class="modal-header">
            <h2 id="workspacePanelTitle">${t('workspaceTitle')}</h2>
            <button class="modal-close" aria-label=${t('closeLabel') as string} @click=${() => this.close()}>
              <span class="mi">close</span>
            </button>
          </div>
          <div class="modal-body">${body}</div>
        </div>
      </div>
      ${this._renderRemovalConfirm()}
      ${this._renderRevokeConfirm()}
    `);
  }
}

defineZenComponent('zen-workspace-panel', ZenWorkspacePanel);
